/**
 * Wave 11u · Batch D — Pull-to-refresh (mobile gesture)
 * ─────────────────────────────────────────────────────
 * Calm, native-feeling pull-to-refresh that only activates on touch
 * devices (no desktop interference). Used at the Member Suite shell
 * level so any page in the Member Suite supports pulling down at the
 * top of the scroll container to trigger a refresh.
 *
 * Usage:
 *   const { wrap, isRefreshing, indicator } = usePullToRefresh(onRefresh);
 *   return wrap(<div>{indicator}{children}</div>);
 *
 * Heuristics:
 *   · Only engages when scrollTop is at 0 AND first finger touchstart
 *     was at the top of the viewport.
 *   · Threshold: 80px pull → triggers refresh.
 *   · Visual: a calm "↻ Refreshing…" pill slides down from the top.
 *   · Respects `prefers-reduced-motion`.
 *   · Auto-disables when desktop (hover: hover) or no touch support.
 */
import React from "react";
import { RefreshCw } from "lucide-react";

const THRESHOLD = 80;       // px pull required to trigger refresh
const MAX_PULL = 140;       // visual cap on indicator drop distance

export function usePullToRefresh(onRefresh) {
  const [pull, setPull] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const startY = React.useRef(null);
  const activeRef = React.useRef(false);
  const containerRef = React.useRef(null);

  const isTouchDevice = React.useMemo(
    () => typeof window !== "undefined" && "ontouchstart" in window && window.matchMedia("(hover: none)").matches,
    [],
  );

  React.useEffect(() => {
    if (!isTouchDevice) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      // Only engage from top of scroll.
      if (window.scrollY > 0) { startY.current = null; activeRef.current = false; return; }
      startY.current = e.touches[0].clientY;
      activeRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || refreshing) return;
      const dy = e.touches[0].clientY - (startY.current || 0);
      if (dy > 0 && window.scrollY === 0) {
        // Dampen the pull for a natural feel.
        const eased = Math.min(MAX_PULL, dy * 0.55);
        setPull(eased);
        if (dy > 10) e.preventDefault(); // prevent rubber-band noise on iOS
      } else {
        setPull(0);
      }
    };

    const onTouchEnd = async () => {
      if (!activeRef.current || refreshing) { activeRef.current = false; return; }
      activeRef.current = false;
      const triggered = pull >= THRESHOLD;
      if (triggered) {
        setRefreshing(true);
        setPull(THRESHOLD); // hold indicator at threshold
        try { await onRefresh?.(); } catch { /* swallow — UI should self-recover */ }
        // Calm cooldown so the user perceives the refresh actually happened.
        await new Promise((r) => setTimeout(r, 350));
        setRefreshing(false);
      }
      setPull(0);
      startY.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isTouchDevice, onRefresh, pull, refreshing]);

  const indicator = isTouchDevice ? (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-1/2 -translate-x-1/2 z-40 transition-opacity"
      style={{
        transform: `translate(-50%, ${Math.max(0, pull - 40)}px)`,
        opacity: Math.min(1, pull / THRESHOLD),
      }}
      data-testid="pull-to-refresh-indicator"
    >
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm border border-[rgba(0,0,0,0.08)] shadow-sm font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]">
        <RefreshCw
          className={`size-3 ${refreshing ? "animate-spin" : ""}`}
          strokeWidth={1.5}
          style={{
            transform: refreshing ? undefined : `rotate(${Math.min(180, (pull / THRESHOLD) * 180)}deg)`,
            transition: refreshing ? "none" : "transform 0.12s ease-out",
          }}
        />
        {refreshing ? "Refreshing…" : pull >= THRESHOLD ? "Release to refresh" : "Pull to refresh"}
      </div>
    </div>
  ) : null;

  return {
    bindRef: (el) => { containerRef.current = el; },
    indicator,
    isRefreshing: refreshing,
    isTouchDevice,
  };
}
